import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  CircularProgress,
  Alert,
  Chip,
  ToggleButtonGroup,
  ToggleButton,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TablePagination,
} from '@mui/material';
import { Download, People } from '@mui/icons-material';
import AppHeader from './AppHeader';
import SessionSelector from './SessionSelector';
import { sessionAPI, studentAPI } from '../services/api';

const todayStr = () => new Date().toISOString().slice(0, 10);

const fmtDate = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

const mondayOf = (isoDate) => {
  const d = new Date(`${isoDate}T00:00:00`);
  d.setDate(d.getDate() - ((d.getDay() + 6) % 7));
  return fmtDate(d);
};

const shiftWeek = (isoMonday, weeks) => {
  const d = new Date(`${isoMonday}T00:00:00`);
  d.setDate(d.getDate() + weeks * 7);
  return fmtDate(d);
};

const WEEKDAY_LABELS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri'];

const AttendancePage = () => {
  const [sessions, setSessions] = useState([]);
  const [currentSession, setCurrentSession] = useState(null);
  const [students, setStudents] = useState([]);
  const [records, setRecords] = useState({});
  const [absenceReasons, setAbsenceReasons] = useState([]);
  const [date, setDate] = useState(todayStr());
  const [serviceType, setServiceType] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [absentReport, setAbsentReport] = useState(null);
  const [reportOpen, setReportOpen] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [monthlyLogOpen, setMonthlyLogOpen] = useState(false);
  const [monthlyLogMonth, setMonthlyLogMonth] = useState('2026-09');
  const [monthlyLogDownloading, setMonthlyLogDownloading] = useState(false);
  const [cpseOpen, setCpseOpen] = useState(false);
  const [cpseMonth, setCpseMonth] = useState(todayStr().slice(0, 7));
  const [cpseBusy, setCpseBusy] = useState(false);
  const [cpseReport, setCpseReport] = useState(null);
  const [cpseImportReport, setCpseImportReport] = useState(null);
  const [aideOpen, setAideOpen] = useState(false);
  const [aideWeekStart, setAideWeekStart] = useState(mondayOf(todayStr()));
  const [aideData, setAideData] = useState(null);
  const [aideEdits, setAideEdits] = useState({});
  const [aideBusy, setAideBusy] = useState(false);
  const [aideMsg, setAideMsg] = useState(null);
  const [aideLogMonth, setAideLogMonth] = useState(todayStr().slice(0, 7));

  const loadSessions = async () => {
    try {
      const res = await sessionAPI.getSessions();
      setSessions(res.data.results || res.data || []);
      let cur = null;
      try {
        const curRes = await sessionAPI.getCurrentSession();
        cur = curRes.data;
      } catch (_) {}
      setCurrentSession(cur);
    } catch (e) {
      setError(e.message);
    }
  };

  useEffect(() => {
    loadSessions();
  }, []);

  useEffect(() => {
    if (!currentSession?.id) return;
    sessionAPI.getAbsenceReasons().then((res) => setAbsenceReasons(res.data.results || res.data || [])).catch(() => {});
  }, [currentSession?.id]);

  useEffect(() => {
    let cancelled = false;
    if (!currentSession?.id) {
      setStudents([]);
      setRecords({});
      return () => { cancelled = true; };
    }
    setLoading(true);
    const params = { session: currentSession.id, page_size: 5000 };
    if (serviceType) params.service_type = serviceType;
    studentAPI.getStudents(currentSession.id, { ...params, active_only: 'true' })
      .then((res) => {
        if (cancelled) return;
        setStudents(res.data.results || res.data || []);
      })
      .catch((e) => {
        if (!cancelled) setError(e.response?.data?.detail || e.message);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => { cancelled = true; };
  }, [currentSession?.id, serviceType]);

  useEffect(() => {
    if (!currentSession?.id || !date) {
      setRecords({});
      return;
    }
    sessionAPI.getAttendance({ session: currentSession.id, date })
      .then((res) => {
        const list = res.data.results || res.data || [];
        const byStudent = {};
        list.forEach((r) => { byStudent[r.student] = r; });
        setRecords(byStudent);
      })
      .catch(() => setRecords({}));
  }, [currentSession?.id, date]);

  const handleSessionChange = async (sessionId) => {
    if (!sessionId) return;
    try {
      await sessionAPI.setActiveSession(sessionId);
      const res = await sessionAPI.getCurrentSession();
      setCurrentSession(res.data);
    } catch (e) {
      console.error(e);
    }
  };

  const getRecord = (studentId) => records[studentId] || null;

  const setStatus = async (studentId, status, absenceReasonId = null) => {
    const rec = getRecord(studentId);
    const payload = {
      date,
      student: studentId,
      status,
      absence_reason: status === 'absent' ? (absenceReasonId || absenceReasons.find((r) => r.reason_code === 'UNKNOWN')?.reason_code) : null,
    };
    try {
      if (rec?.id) {
        await sessionAPI.updateAttendanceRecord(rec.id, { ...payload, student: studentId });
      } else {
        await sessionAPI.createAttendanceRecord(payload);
      }
      const res = await sessionAPI.getAttendance({ session: currentSession.id, date });
      const list = res.data.results || res.data || [];
      const byStudent = {};
      list.forEach((r) => { byStudent[r.student] = r; });
      setRecords(byStudent);
    } catch (e) {
      setError(e.response?.data?.detail || e.message);
    }
  };

  const setLateOrEarly = async (studentId, field, value) => {
    const rec = getRecord(studentId);
    const current = rec || { student: studentId, date, status: 'present' };
    const payload = {
      date,
      student: studentId,
      status: current.status,
      late_arrival_time: field === 'late' ? value : (current.late_arrival_time || null),
      early_dismissal_time: field === 'early' ? value : (current.early_dismissal_time || null),
      absence_reason: current.absence_reason || null,
    };
    try {
      if (rec?.id) {
        await sessionAPI.updateAttendanceRecord(rec.id, payload);
      } else {
        await sessionAPI.createAttendanceRecord(payload);
      }
      const res = await sessionAPI.getAttendance({ session: currentSession.id, date });
      const list = res.data.results || res.data || [];
      const byStudent = {};
      list.forEach((r) => { byStudent[r.student] = r; });
      setRecords(byStudent);
    } catch (e) {
      setError(e.response?.data?.detail || e.message);
    }
  };

  const markAllPresent = async () => {
    for (const s of students) {
      const rec = getRecord(s.id);
      if (!rec || rec.status === 'absent') {
        await setStatus(s.id, 'present');
      }
    }
  };

  const openDailyAbsentReport = async () => {
    try {
      const res = await sessionAPI.getAttendanceDailyAbsentReport({ date, session: currentSession?.id });
      setAbsentReport(res.data);
      setReportOpen(true);
    } catch (e) {
      setError(e.response?.data?.detail || e.message);
    }
  };

  const exportDailyAbsentCsv = async () => {
    try {
      const res = await sessionAPI.getAttendanceDailyAbsentReportCsv({ date, session: currentSession?.id });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = `daily_absent_${date}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      setError(e.response?.data?.detail || e.message);
    }
  };

  const monthlyLogMonthOptions = [
    { value: '2026-09', label: 'September 2026' },
    { value: '2026-10', label: 'October 2026' },
    { value: '2026-11', label: 'November 2026' },
    { value: '2026-12', label: 'December 2026' },
    { value: '2027-01', label: 'January 2027' },
    { value: '2027-02', label: 'February 2027' },
    { value: '2027-03', label: 'March 2027' },
    { value: '2027-04', label: 'April 2027' },
    { value: '2027-05', label: 'May 2027' },
    { value: '2027-06', label: 'June 2027' },
  ];

  const downloadMonthlyAttendanceLogPdf = async () => {
    const [y, m] = monthlyLogMonth.split('-').map(Number);
    setMonthlyLogDownloading(true);
    try {
      const res = await sessionAPI.getMonthlyAttendanceLogPdf({
        session: currentSession?.id,
        month: m,
        year: y,
      });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = `monthly_attendance_log_${y}_${String(m).padStart(2, '0')}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
      setMonthlyLogOpen(false);
    } catch (e) {
      setError(e.response?.data?.detail || e.message);
    } finally {
      setMonthlyLogDownloading(false);
    }
  };

  const checkCpseExport = async () => {
    const [y, m] = cpseMonth.split('-').map(Number);
    setCpseBusy(true);
    setCpseReport(null);
    try {
      const res = await sessionAPI.getCpseAttendanceExportReport({ session: currentSession?.id, month: m, year: y });
      setCpseReport(res.data);
    } catch (e) {
      setError(e.response?.data?.detail || e.message);
    } finally {
      setCpseBusy(false);
    }
  };

  const downloadCpseXml = async (force = false) => {
    const [y, m] = cpseMonth.split('-').map(Number);
    setCpseBusy(true);
    try {
      const params = { session: currentSession?.id, month: m, year: y };
      if (force) params.force = '1';
      const res = await sessionAPI.getCpseAttendanceExportXml(params);
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = `cpse_attendance_${y}_${String(m).padStart(2, '0')}.xml`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      // Blob error responses need to be read as text to surface the report detail
      if (e.response?.data instanceof Blob) {
        try {
          const parsed = JSON.parse(await e.response.data.text());
          setCpseReport(parsed);
          setError(parsed.detail || 'Export blocked — see issues below.');
        } catch (_) {
          setError('Export failed.');
        }
      } else {
        setError(e.response?.data?.detail || e.message);
      }
    } finally {
      setCpseBusy(false);
    }
  };

  const importCpseFile = async (file) => {
    if (!file) return;
    setCpseBusy(true);
    setCpseImportReport(null);
    try {
      const res = await sessionAPI.importCpseEnrollments(currentSession?.id, file);
      setCpseImportReport(res.data);
    } catch (e) {
      setError(e.response?.data?.detail || e.message);
    } finally {
      setCpseBusy(false);
    }
  };

  const loadAideWeek = async (weekStart = aideWeekStart) => {
    if (!currentSession?.id) return;
    setAideBusy(true);
    setAideMsg(null);
    try {
      const res = await sessionAPI.getCpseAideWeek({ session: currentSession.id, week_start: weekStart });
      setAideData(res.data);
      // Nurse rows: pre-fill empty present-day cells with the agency name (confirm/override, saved on Save Week)
      const prefills = {};
      (res.data.rows || []).forEach((row) => {
        if (row.subtype !== 'nurse' || !row.provider_name) return;
        (res.data.dates || []).forEach((d) => {
          const cell = row.assignments[d] || {};
          if (!cell.staff_name && cell.child_present !== false && !res.data.off_days[d]) {
            prefills[`${row.enrollment_id}|${d}`] = row.provider_name;
          }
        });
      });
      setAideEdits(prefills);
    } catch (e) {
      setError(e.response?.data?.detail || e.message);
    } finally {
      setAideBusy(false);
    }
  };

  useEffect(() => {
    if (aideOpen && currentSession?.id) loadAideWeek(aideWeekStart);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [aideOpen, aideWeekStart, currentSession?.id]);

  const saveAideWeek = async () => {
    if (!aideData) return;
    setAideBusy(true);
    setAideMsg(null);
    try {
      let saves = 0;
      for (const row of aideData.rows || []) {
        // group changed dates by staff name (bulk-set takes one name per call)
        const byName = {};
        for (const d of aideData.dates || []) {
          const key = `${row.enrollment_id}|${d}`;
          if (!(key in aideEdits)) continue;
          const cell = row.assignments[d] || {};
          if (cell.child_present === false || aideData.off_days[d]) continue;
          const name = (aideEdits[key] || '').trim();
          if (!name || name === cell.staff_name) continue;
          (byName[name] = byName[name] || []).push(d);
        }
        for (const [name, dates] of Object.entries(byName)) {
          await sessionAPI.bulkSetCpseAideAssignments({
            enrollment: row.enrollment_id,
            staff_name: name,
            credential: row.subtype === 'nurse' ? (row.credential || '') : '',
            dates,
          });
          saves += dates.length;
        }
      }
      setAideMsg(saves ? `Saved ${saves} day(s).` : 'Nothing to save.');
      await loadAideWeek(aideWeekStart);
    } catch (e) {
      setError(e.response?.data?.detail || e.message);
    } finally {
      setAideBusy(false);
    }
  };

  const downloadAideLogXlsx = async () => {
    setAideBusy(true);
    try {
      const res = await sessionAPI.getCpseAideLogXlsx({ session: currentSession?.id, month: aideLogMonth });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = `aide_log_${aideLogMonth.replace('-', '_')}.xlsx`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      setError(e.response?.data?.detail || e.message);
    } finally {
      setAideBusy(false);
    }
  };

  const cpseBlockingIssues = cpseReport ? [
    ...(cpseReport.students_missing_esid || []).map((r) => `No ESID: ${r.student_name}`),
    ...(cpseReport.classrooms_missing_identifier || []).map((r) => `Classroom "${r.classroom}" missing teacher NPI/ID${r.teacher ? ` (${r.teacher})` : ''}`),
    ...(cpseReport.students_without_classroom || []).map((r) => `No classroom: ${r.student_name}`),
    ...(cpseReport.missing_attendance || []).map((r) => `Missing attendance: ${r.student_name} — ${r.date}`),
    ...(cpseReport.missing_aide_assignment || []).map((r) => `No ${r.subtype === 'nurse' ? 'nurse' : 'aide'} recorded: ${r.student_name} — ${r.date} (${r.esid})`),
  ] : [];

  const paginatedStudents = students.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  return (
    <Box>
      <AppHeader backTo="/" rightContent={<Typography variant="subtitle1">Attendance</Typography>} />
      <Box sx={{ p: 2 }}>
        <SessionSelector sessions={sessions} currentSession={currentSession} onSessionChange={handleSessionChange} />
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'center', mt: 2 }}>
          <TextField
            label="Date"
            type="date"
            value={date}
            onChange={(e) => setDate(e.target.value)}
            size="small"
            InputLabelProps={{ shrink: true }}
          />
          <FormControl size="small" sx={{ minWidth: 160 }}>
            <InputLabel>Service type</InputLabel>
            <Select value={serviceType} label="Service type" onChange={(e) => setServiceType(e.target.value)}>
              <MenuItem value="">All</MenuItem>
              <MenuItem value="center_based">Center-based</MenuItem>
              <MenuItem value="related_service">Related service</MenuItem>
            </Select>
          </FormControl>
          <Button variant="outlined" onClick={markAllPresent} disabled={!currentSession || loading}>
            Mark all present
          </Button>
          <Button variant="outlined" startIcon={<People />} onClick={openDailyAbsentReport} disabled={!currentSession}>
            Daily absent report
          </Button>
          <Button variant="outlined" startIcon={<Download />} onClick={() => setMonthlyLogOpen(true)} disabled={!currentSession}>
            Monthly Attendance Log
          </Button>
          <Button variant="outlined" startIcon={<Download />} onClick={() => { setCpseReport(null); setCpseImportReport(null); setCpseOpen(true); }} disabled={!currentSession}>
            CPSE Portal Upload
          </Button>
          <Button variant="outlined" startIcon={<People />} onClick={() => { setAideMsg(null); setAideOpen(true); }} disabled={!currentSession}>
            1:1 Aide Log
          </Button>
          {reportOpen && absentReport && (
            <Button variant="outlined" startIcon={<Download />} onClick={exportDailyAbsentCsv}>
              Export absent CSV
            </Button>
          )}
        </Box>

        <Dialog open={monthlyLogOpen} onClose={() => !monthlyLogDownloading && setMonthlyLogOpen(false)} maxWidth="xs" fullWidth>
          <DialogTitle>Monthly Attendance Log</DialogTitle>
          <DialogContent>
            <FormControl size="small" fullWidth sx={{ mt: 1 }}>
              <InputLabel>Month</InputLabel>
              <Select
                value={monthlyLogMonth}
                label="Month"
                onChange={(e) => setMonthlyLogMonth(e.target.value)}
              >
                {monthlyLogMonthOptions.map((opt) => (
                  <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => !monthlyLogDownloading && setMonthlyLogOpen(false)}>Cancel</Button>
            <Button variant="contained" onClick={downloadMonthlyAttendanceLogPdf} disabled={monthlyLogDownloading}>
              {monthlyLogDownloading ? 'Downloading…' : 'Download PDF'}
            </Button>
          </DialogActions>
        </Dialog>
        <Dialog open={cpseOpen} onClose={() => !cpseBusy && setCpseOpen(false)} maxWidth="sm" fullWidth>
          <DialogTitle>CPSE Portal attendance upload (XML)</DialogTitle>
          <DialogContent>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Generates the center-based (CB) classroom attendance file for upload at CPSE Portal.
              Every student needs an ESID and every classroom needs a teacher NPI / CPSE ID.
            </Typography>
            <TextField
              label="Month"
              type="month"
              size="small"
              value={cpseMonth}
              onChange={(e) => { setCpseMonth(e.target.value); setCpseReport(null); }}
              InputLabelProps={{ shrink: true }}
              sx={{ mr: 2 }}
            />
            <Button variant="outlined" onClick={checkCpseExport} disabled={cpseBusy || !cpseMonth}>
              Check readiness
            </Button>
            {cpseReport && (
              <Box sx={{ mt: 2 }}>
                <Typography variant="body2">
                  School days: {cpseReport.school_days} · Rows: {cpseReport.rows} (present {cpseReport.present}, absent {cpseReport.absent}, closed {cpseReport.closed}) · Students: {cpseReport.students_included}
                </Typography>
                {cpseBlockingIssues.length > 0 ? (
                  <Alert severity="warning" sx={{ mt: 1, maxHeight: 220, overflow: 'auto' }}>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      {cpseBlockingIssues.length} issue(s) — affected rows will be left out of the file:
                    </Typography>
                    {cpseBlockingIssues.slice(0, 50).map((msg, i) => (
                      <Typography key={i} variant="body2">• {msg}</Typography>
                    ))}
                    {cpseBlockingIssues.length > 50 && (
                      <Typography variant="body2">…and {cpseBlockingIssues.length - 50} more</Typography>
                    )}
                  </Alert>
                ) : (
                  <Alert severity="success" sx={{ mt: 1 }}>Ready — no issues found.</Alert>
                )}
              </Box>
            )}
            <Box sx={{ mt: 3, pt: 2, borderTop: 1, borderColor: 'divider' }}>
              <Typography variant="subtitle2">Import ESIDs from CPSE Portal</Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                Upload the enrollment export (CSV or Excel) from CPSE Portal to fill in student ESIDs and counties.
              </Typography>
              <Button variant="outlined" component="label" disabled={cpseBusy}>
                Upload enrollment file
                <input type="file" hidden accept=".csv,.xlsx,.xls" onChange={(e) => { importCpseFile(e.target.files?.[0]); e.target.value = ''; }} />
              </Button>
              {cpseImportReport && (
                <Alert severity={cpseImportReport.unmatched?.length ? 'warning' : 'success'} sx={{ mt: 1, maxHeight: 200, overflow: 'auto' }}>
                  <Typography variant="body2">
                    Created {cpseImportReport.created?.length ?? 0}, updated {cpseImportReport.updated?.length ?? 0}, unmatched {cpseImportReport.unmatched?.length ?? 0}.
                  </Typography>
                  {(cpseImportReport.unmatched || []).map((row, i) => (
                    <Typography key={i} variant="body2">• Not matched: {row.name} ({row.esid})</Typography>
                  ))}
                  {(cpseImportReport.warnings || []).map((row, i) => (
                    <Typography key={`w${i}`} variant="body2">• {row.name}: {row.reason}</Typography>
                  ))}
                </Alert>
              )}
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => !cpseBusy && setCpseOpen(false)}>Close</Button>
            {cpseReport && cpseBlockingIssues.length > 0 && (
              <Button color="warning" onClick={() => downloadCpseXml(true)} disabled={cpseBusy}>
                Download anyway
              </Button>
            )}
            <Button variant="contained" onClick={() => downloadCpseXml(false)} disabled={cpseBusy || !cpseMonth}>
              {cpseBusy ? 'Working…' : 'Download XML'}
            </Button>
          </DialogActions>
        </Dialog>
        <Dialog open={aideOpen} onClose={() => !aideBusy && setAideOpen(false)} maxWidth="lg" fullWidth>
          <DialogTitle>1:1 Aide/Nurse Log</DialogTitle>
          <DialogContent>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'center', mb: 2 }}>
              <Button size="small" onClick={() => setAideWeekStart(shiftWeek(aideWeekStart, -1))} disabled={aideBusy}>‹ Prev week</Button>
              <TextField
                label="Week of"
                type="date"
                size="small"
                value={aideWeekStart}
                onChange={(e) => e.target.value && setAideWeekStart(mondayOf(e.target.value))}
                InputLabelProps={{ shrink: true }}
              />
              <Button size="small" onClick={() => setAideWeekStart(shiftWeek(aideWeekStart, 1))} disabled={aideBusy}>Next week ›</Button>
              <Box sx={{ flexGrow: 1 }} />
              <TextField
                label="Report month"
                type="month"
                size="small"
                value={aideLogMonth}
                onChange={(e) => setAideLogMonth(e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
              <Button variant="outlined" startIcon={<Download />} onClick={downloadAideLogXlsx} disabled={aideBusy || !aideLogMonth}>
                Download Monthly Log
              </Button>
            </Box>
            {aideMsg && <Alert severity="success" sx={{ mb: 1 }} onClose={() => setAideMsg(null)}>{aideMsg}</Alert>}
            {aideBusy && !aideData && <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}><CircularProgress /></Box>}
            {aideData && (aideData.rows || []).length === 0 && (
              <Typography color="text.secondary">No students with aide or nurse enrollments in this session.</Typography>
            )}
            {aideData && (aideData.rows || []).length > 0 && (
              <TableContainer component={Paper}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ minWidth: 220 }}>Student</TableCell>
                      {(aideData.dates || []).map((d, i) => (
                        <TableCell key={d} align="center" sx={{ minWidth: 130 }}>
                          {WEEKDAY_LABELS[i]} {d.slice(5).replace('-', '/')}
                          {aideData.off_days[d] && (
                            <Typography variant="caption" display="block" color="text.secondary">{aideData.off_days[d]}</Typography>
                          )}
                        </TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {aideData.rows.map((row) => (
                      <TableRow key={row.enrollment_id}>
                        <TableCell>
                          {row.student_name}{' '}
                          <Chip
                            size="small"
                            label={row.subtype === 'nurse' ? `Nurse${row.credential ? ` · ${row.credential}` : ''}` : 'Aide'}
                            color={row.subtype === 'nurse' ? 'info' : 'default'}
                            variant="outlined"
                          />
                          {row.provider_name && (
                            <Typography variant="caption" display="block" color="text.secondary">{row.provider_name}</Typography>
                          )}
                        </TableCell>
                        {(aideData.dates || []).map((d) => {
                          const key = `${row.enrollment_id}|${d}`;
                          const cell = row.assignments[d] || {};
                          if (aideData.off_days[d]) {
                            return <TableCell key={d} align="center" sx={{ bgcolor: 'grey.100' }}>—</TableCell>;
                          }
                          if (cell.child_present === false) {
                            return (
                              <TableCell key={d} align="center" sx={{ bgcolor: 'grey.200' }}>
                                <Typography variant="caption" color="text.secondary">Child absent</Typography>
                              </TableCell>
                            );
                          }
                          const value = key in aideEdits ? aideEdits[key] : (cell.staff_name || '');
                          return (
                            <TableCell key={d} sx={{ p: 0.5 }}>
                              <TextField
                                size="small"
                                fullWidth
                                value={value}
                                placeholder={cell.child_present == null ? 'no attendance yet' : ''}
                                onChange={(e) => setAideEdits((prev) => ({ ...prev, [key]: e.target.value }))}
                                sx={{ bgcolor: value.trim() ? undefined : '#fff9c4' }}
                              />
                            </TableCell>
                          );
                        })}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
            <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
              Yellow = child present but no aide recorded (blocks the CPSE upload). Gray = child absent or school closed — no entry needed.
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => !aideBusy && setAideOpen(false)}>Close</Button>
            <Button variant="contained" onClick={saveAideWeek} disabled={aideBusy || !aideData}>
              {aideBusy ? 'Working…' : 'Save Week'}
            </Button>
          </DialogActions>
        </Dialog>
        {error && <Alert severity="error" sx={{ mt: 2 }} onClose={() => setError(null)}>{error}</Alert>}
        {loading && <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}><CircularProgress /></Box>}
        {!loading && (
          <>
            <TableContainer component={Paper} sx={{ mt: 2 }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell>Name (Last, First)</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Late arrival</TableCell>
                    <TableCell>Early dismissal</TableCell>
                    <TableCell>Absence reason</TableCell>
                    <TableCell>Notes</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {paginatedStudents.map((s) => {
                    const rec = getRecord(s.id);
                    const status = rec?.status || 'present';
                    return (
                      <TableRow key={s.id}>
                        <TableCell>{s.last_name}, {s.first_name}</TableCell>
                        <TableCell>
                          <ToggleButtonGroup
                            size="small"
                            value={status}
                            exclusive
                            onChange={(_, v) => v != null && setStatus(s.id, v)}
                          >
                            <ToggleButton value="present">Present</ToggleButton>
                            <ToggleButton value="absent">Absent</ToggleButton>
                          </ToggleButtonGroup>
                        </TableCell>
                        <TableCell>
                          <TextField
                            type="time"
                            size="small"
                            sx={{ width: 100 }}
                            InputLabelProps={{ shrink: true }}
                            value={rec?.late_arrival_time ? rec.late_arrival_time.slice(0, 5) : ''}
                            onChange={(e) => setLateOrEarly(s.id, 'late', e.target.value || null)}
                            disabled={status === 'absent'}
                          />
                        </TableCell>
                        <TableCell>
                          <TextField
                            type="time"
                            size="small"
                            sx={{ width: 100 }}
                            InputLabelProps={{ shrink: true }}
                            value={rec?.early_dismissal_time ? rec.early_dismissal_time.slice(0, 5) : ''}
                            onChange={(e) => setLateOrEarly(s.id, 'early', e.target.value || null)}
                            disabled={status === 'absent'}
                          />
                        </TableCell>
                        <TableCell>
                          <Select
                            size="small"
                            sx={{ minWidth: 140 }}
                            value={rec?.absence_reason ?? ''}
                            displayEmpty
                            disabled={status === 'present'}
                            onChange={(e) => setStatus(s.id, 'absent', e.target.value)}
                          >
                            <MenuItem value="">—</MenuItem>
                            {absenceReasons.map((r) => (
                              <MenuItem key={r.reason_code} value={r.reason_code}>{r.reason_label}</MenuItem>
                            ))}
                          </Select>
                        </TableCell>
                        <TableCell>
                          <TextField size="small" placeholder="Notes" sx={{ width: 120 }} defaultValue={rec?.notes} onBlur={(e) => {
                            const v = e.target.value?.trim();
                            if (rec?.id && v !== (rec.notes || '')) {
                              sessionAPI.updateAttendanceRecord(rec.id, { ...rec, notes: v || null }).then(() => {
                                setRecords((prev) => ({ ...prev, [s.id]: { ...rec, notes: v || null } }));
                              });
                            }
                          }} />
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
            <TablePagination
              component="div"
              count={students.length}
              page={page}
              onPageChange={(_, p) => setPage(p)}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
              rowsPerPageOptions={[25, 50, 100]}
            />
          </>
        )}
        {!loading && students.length === 0 && currentSession && (
          <Typography color="text.secondary" sx={{ mt: 2 }}>No active students.</Typography>
        )}
      </Box>

      <Dialog open={reportOpen} onClose={() => setReportOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Daily absent report — {date}</DialogTitle>
        <DialogContent>
          {absentReport && (
            <Box>
              <Typography variant="subtitle2" sx={{ mt: 1 }}>Absent ({absentReport.absent?.length ?? 0})</Typography>
              <TableContainer><Table size="small">
                <TableHead><TableRow><TableCell>Student</TableCell><TableCell>District</TableCell><TableCell>Service type</TableCell><TableCell>Absence reason</TableCell><TableCell>Contact</TableCell></TableRow></TableHead>
                <TableBody>
                  {(absentReport.absent || []).map((row, i) => (
                    <TableRow key={i}>
                      <TableCell>{row.student_name}</TableCell>
                      <TableCell>{row.district}</TableCell>
                      <TableCell>{row.service_type}</TableCell>
                      <TableCell>{row.absence_reason}</TableCell>
                      <TableCell>{row.mother_cell || row.father_cell || row.home_phone || row.email || '—'}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table></TableContainer>
              <Typography variant="subtitle2" sx={{ mt: 2 }}>Missing attendance entry ({absentReport.missing_attendance_entry?.length ?? 0})</Typography>
              <TableContainer><Table size="small">
                <TableHead><TableRow><TableCell>Student</TableCell><TableCell>District</TableCell><TableCell>Service type</TableCell><TableCell>Contact</TableCell></TableRow></TableHead>
                <TableBody>
                  {(absentReport.missing_attendance_entry || []).map((row, i) => (
                    <TableRow key={i}>
                      <TableCell>{row.student_name}</TableCell>
                      <TableCell>{row.district}</TableCell>
                      <TableCell>{row.service_type}</TableCell>
                      <TableCell>{row.mother_cell || row.father_cell || row.home_phone || row.email || '—'}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table></TableContainer>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setReportOpen(false)}>Close</Button>
          {absentReport && <Button onClick={exportDailyAbsentCsv}>Export CSV</Button>}
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AttendancePage;
